import { useTranslation } from '@jq4race/i18n';
import type { PhotoAngle, PhotoUploadUrlsResponse } from '@jq4race/shared';
import { useCallback, useRef, useState } from 'react';
import type { PhotoSink } from '../../domain/ports/photo-sink.js';
import { RemotePhotoSink } from '../../infrastructure/remote-photo-sink.js';
import type { SlotState } from '../components/PhotoCaptureGrid';

export interface UsePhotoUploadOptions {
  /** Angles this instance manages (order determines `photoKeys` order). */
  angles: readonly PhotoAngle[];
  /** Issues fresh presigned upload targets, one per angle. */
  requestUrls: () => Promise<PhotoUploadUrlsResponse>;
  /**
   * Pluggable upload destination (design D2 / pwa-offline-first Slice 4).
   * Defaults to `new RemotePhotoSink(requestUrls)` — today's
   * upload-at-capture behavior, used by Flash and (for now) by single
   * check-in. Passing a `LocalPhotoSink` here (a future CheckinPage change,
   * Slice 6) switches to upload-at-sync without touching this hook.
   */
  sink?: PhotoSink;
}

export interface UsePhotoUploadResult {
  slots: Record<PhotoAngle, SlotState>;
  /** True once every managed angle has reached the 'done' status. */
  allPhotosDone: boolean;
  /** Angle currently open in the camera viewfinder, or null. */
  activeAngle: PhotoAngle | null;
  onSlotCapture: (angle: PhotoAngle) => void;
  onSlotRetake: (angle: PhotoAngle) => void;
  onCameraCapture: (blob: Blob) => Promise<void>;
  onCameraCancel: () => void;
  /** Storage keys of every slot currently 'done', in angle order (may be a subset of `angles`). */
  photoKeys: string[];
  /** Revokes thumbnail object URLs and resets all slots back to empty. Call after a successful submit/add. */
  reset: () => void;
}

function buildEmptySlots(angles: readonly PhotoAngle[]): Record<PhotoAngle, SlotState> {
  return Object.fromEntries(angles.map((a) => [a, { status: 'empty' }])) as Record<
    PhotoAngle,
    SlotState
  >;
}

/**
 * usePhotoUpload — shared angle-photo capture+upload orchestration (D2,
 * event-photo-capture Slice B). Extracted from the inline block previously
 * living in CheckinPage so both single check-in and Flash's angle grid share
 * one source of truth: slot state machine, presigned-target caching (<14min
 * re-issue), capture-at-add upload timing, and the transparent single retry.
 *
 * Owns component state (not react-hook-form) — mirrors the ADR-22-5 decision
 * that photo capture state is orthogonal to the surrounding form's validation
 * lifecycle.
 */
export function usePhotoUpload({
  angles,
  requestUrls,
  sink,
}: UsePhotoUploadOptions): UsePhotoUploadResult {
  const { t } = useTranslation('checkin');
  const [slots, setSlots] = useState<Record<PhotoAngle, SlotState>>(() => buildEmptySlots(angles));
  const [activeAngle, setActiveAngle] = useState<PhotoAngle | null>(null);

  // Derived, not stored — recomputed from `slots` on every render so it can
  // never drift out of sync (previously a manually-tracked boolean).
  const allPhotosDone = angles.every((a) => slots[a]?.status === 'done');

  // Constructed once, on first render — matches the previous targetsRef
  // lifetime exactly (requestUrls was never in a dependency array either).
  // A caller-injected `sink` (LocalPhotoSink, a future PhotoSink adapter)
  // always wins over the default RemotePhotoSink.
  const sinkRef = useRef<PhotoSink | null>(null);
  if (!sinkRef.current) {
    sinkRef.current = sink ?? new RemotePhotoSink(requestUrls);
  }

  async function uploadSlot(angle: PhotoAngle, blob: Blob): Promise<void> {
    setSlots((prev) => ({
      ...prev,
      [angle]: {
        ...prev[angle],
        status: 'uploading',
        blob,
        thumbnailUrl: URL.createObjectURL(blob),
      },
    }));

    try {
      // sinkRef.current is always set above before this can run.
      const { key } = await (sinkRef.current as PhotoSink).upload(angle, blob);

      setSlots((prev) => ({
        ...prev,
        [angle]: {
          ...prev[angle],
          status: 'done',
          key,
        },
      }));
    } catch {
      // Never swallowed at the port level (RemotePhotoSink/LocalPhotoSink
      // both propagate their failures) — surfaced to the operator here as
      // a visible slot error, per the critical "zero silent photo loss"
      // requirement.
      setSlots((prev) => ({
        ...prev,
        [angle]: {
          ...prev[angle],
          status: 'error',
          errorMessage: t('incidentPhotos.uploadFailed'),
        },
      }));
    }
  }

  // Stable ref so onCameraCapture can depend on activeAngle only.
  const uploadSlotRef = useRef(uploadSlot);
  uploadSlotRef.current = uploadSlot;

  const onSlotCapture = useCallback((angle: PhotoAngle) => {
    setActiveAngle(angle);
  }, []);

  const onSlotRetake = useCallback((angle: PhotoAngle) => {
    setSlots((prev) => ({ ...prev, [angle]: { status: 'empty' } }));
    setActiveAngle(angle);
  }, []);

  const onCameraCapture = useCallback(
    async (blob: Blob) => {
      if (!activeAngle) return;
      const angle = activeAngle;
      setActiveAngle(null);
      await uploadSlotRef.current(angle, blob);
    },
    [activeAngle],
  );

  const onCameraCancel = useCallback(() => {
    setActiveAngle(null);
  }, []);

  const reset = useCallback(() => {
    setSlots((prev) => {
      for (const angle of angles) {
        const url = prev[angle]?.thumbnailUrl;
        if (url) URL.revokeObjectURL(url);
      }
      return buildEmptySlots(angles);
    });
    setActiveAngle(null);
    sinkRef.current?.reset?.();
  }, [angles]);

  const photoKeys = angles.map((a) => slots[a]?.key).filter((key): key is string => Boolean(key));

  return {
    slots,
    allPhotosDone,
    activeAngle,
    onSlotCapture,
    onSlotRetake,
    onCameraCapture,
    onCameraCancel,
    photoKeys,
    reset,
  };
}
